import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import {
  getStudentByIdAPI,
  removeStudentAPI,
} from "../../../services/portal/admin/studentService";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../../utility/ToastMessage";
import Loader from "../../../components/Loader";
import StudentDetailsBox from "../../../components/portal/StudentDetailsBox";
import StudentDetailsManage from "../../../components/portal/StudentDetailsManage";
import StudentContactNumberManage from "../../../components/portal/StudentContactNumberManage";
import StudentResetPasswordManage from "../../../components/portal/StudentResetPasswordManage";
import StudentPaymentHistory from "../../../components/portal/StudentPaymentHistory";
import { RoutePaths } from "../../../utility/RoutePaths";
import RemoveConfirmationDialog from "../../../components/portal/RemoveConfirmationDialog";
import StudentAssignClassScheduleManage from "../../../components/portal/StudentAssignClassScheduleManage";

const StudentDetailsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const studentId = searchParams.get("studentId");
  const [activeTab, setActiveTab] = useState(1);
  const [loading, setLoading] = useState(false);
  const [student, setStudent] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const loadStudentDetails = useCallback(async () => {
    if (!studentId) return;

    setLoading(true);
    try {
      const res = await getStudentByIdAPI(studentId);
      if (res && res.IsSuccessful && res.Data) {
        const studentData = res.Data;
        setStudent(studentData);
      } else {
        showErrorToastMsg("Student not found");
      }
    } catch (error) {
      console.error("Error loading student details:", error);
      showErrorToastMsg("Failed to load student details");
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (studentId) {
      loadStudentDetails();
    }
  }, [studentId, loadStudentDetails]);

  const handleDelete = (holiday) => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const res = await removeStudentAPI(student.UserId);

      if (res.IsSuccessful) {
        showSuccessToastMsg("Data deleted successfully");
        navigate(`${RoutePaths.ADMIN_STUDENT_MANAGE_PAGE}`);
      } else {
        showErrorToastMsg(res.Message || "Failed to delete data");
      }

      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Error deleting holiday:", error);
      showErrorToastMsg("Failed to delete data");
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  if (!studentId) {
    return (
      <DashboardLayout
        title="Student Details"
        subtitle="Student ID is required"
      >
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Student ID is required</p>
        </div>
      </DashboardLayout>
    );
  }

  if (loading && !student) {
    return (
      <DashboardLayout
        title="Student Details"
        subtitle="Loading student information"
      >
        <div className="flex justify-center items-center py-12">
          <Loader />
        </div>
      </DashboardLayout>
    );
  }

  if (!student) {
    return (
      <DashboardLayout title="Student Details" subtitle="Student not found">
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Student not found</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Student Details">
      <div>
        <div className="mb-4">
          <button
            onClick={() => navigate(RoutePaths.ADMIN_STUDENT_MANAGE_PAGE)}
            className="text-sm font-medium text-primary-600 hover:text-primary-800"
          >
            ← Back to Student List
          </button>
        </div>

        <StudentDetailsBox studentDetails={student} />

        {/* Tabs */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab(1)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 1
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Personal & Parent Details
              </button>
              <button
                onClick={() => setActiveTab(2)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 2
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Payment History
              </button>
              <button
                onClick={() => setActiveTab(3)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 3
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Reset Password
              </button>
              <button
                onClick={() => setActiveTab(4)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 4
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Change Mobile Number
              </button>
              <button
                onClick={() => setActiveTab(5)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 5
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Temporary Assigned Schedules
              </button>
              <button
                onClick={() => setActiveTab(6)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 6
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Remove
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Tab 1: Personal & Parent Details */}
            {activeTab === 1 && (
              <StudentDetailsManage studentData={student} isAdminView={true} />
            )}

            {/* Tab 2: Payment History */}
            {activeTab === 2 && (
              <StudentPaymentHistory student={student} isAdminView={true} />
            )}

            {/* Tab 3: Reset Password */}
            {activeTab === 3 && (
              <StudentResetPasswordManage
                student={student}
                isAdminView={true}
              />
            )}

            {/* Tab 4: Change Mobile Number */}
            {activeTab === 4 && (
              <StudentContactNumberManage student={student} />
            )}

            {activeTab === 5 && (
              <StudentAssignClassScheduleManage student={student} />
            )}

            {activeTab === 6 && (
              <div className="flex ">
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
                >
                  {loading ? "Deleting..." : "Delete"}
                </button>
              </div>
            )}
          </div>

          {showDeleteConfirm && (
            <RemoveConfirmationDialog
              loading={loading}
              description={student.FirstName + " " + student.LastName}
              cancelDelete={cancelDelete}
              confirmDelete={confirmDelete}
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StudentDetailsPage;
